import {
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  Theme,
  createStyles,
  fade,
  makeStyles
} from '@material-ui/core/styles';
import {
  ClearRounded,
  EditOutlined,
  FaceRounded,
  FiberManualRecord,
  FiberManualRecordOutlined,
  VisibilityOutlined
} from '@material-ui/icons';
import { Alert, Autocomplete, Pagination } from '@material-ui/lab';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../../app/store';
import { PermissionLabel } from '../../../common/enum/PermissionLabel';
import { ORDER } from '../../../common/enum/order';
import { PaginationProps } from '../../../common/interface/pagination';
import EnhancedTableToolbar from '../../../components/EnhancedTableToolbar';
import getNotFoundText from '../../../functions/getNotFoundText';
import { getCookie, updateCookie } from '../../../functions/manageCookies';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import FaceRetouchingOffRounded from '../../../icon/FaceRetouchingOffRounded';
import Breadcrumb from '../../Breadcrumb';
import loadingSlice from '../../Loading/loadingSlice';
import { Discipline } from '../Discipline/disciplineSlice';
import { Document } from '../Document/documentSlice';
import {
  createDocumentProjectPositionWorkflowStepAsync,
  deleteDocumentProjectPositionWorkflowStepAsync,
  getDocumentStandardWorkflowsAsync,
  getWorkflowStepWithPositionsAsync,
  updateActiveDocumentAsync
} from './standardWorkflowSlice';

export interface DocumentStandardWorkflowParam extends PaginationProps {
  disciplineIds: number[];
  search: string | null;
}

interface FormatData {
  documentId: number;
  projectPositionWorkflowStepId: number;
}

const getSortOptions = (t: TFunction) => [
  { value: 'id', text: t('masterData.standardWorkflow.sortOptions.createdDate') },
  { value: 'no', text: t('masterData.standardWorkflow.sortOptions.documentNo') },
  { value: 'name', text: t('masterData.standardWorkflow.sortOptions.documentName') }
];

export default function StandardWorkflow() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { user } = useSelector((state: RootState) => state.userProfile);
  const {
    headers,
    data: documents,
    count
  } = useSelector((state: RootState) => state.standardWorkflows);
  const { data: disciplines } = useSelector(
    (state: RootState) => state.disciplines
  );

  const hasPermission =
    user?.permissions.some(
      (permission) => permission === PermissionLabel.AccessStandardWorkflow
    ) ?? false;

  // Prepare for rendered header of the table
  const creatorHeader = headers.filter(
    ({ id, projectPositionWorkflowSteps }) =>
      id == 4 && projectPositionWorkflowSteps.length > 0
  );
  const otherHeaders = headers.filter(
    ({ id, projectPositionWorkflowSteps }) =>
      id != 4 && projectPositionWorkflowSteps.length > 0
  );
  const formatHeaders = [...creatorHeader, ...otherHeaders];

  // Prepare for rendered table cell in the table body
  const projectPositionWorkflowStepIds: number[] = [];
  formatHeaders.forEach(({ projectPositionWorkflowSteps }) => {
    projectPositionWorkflowSteps.forEach(({ id }) =>
      projectPositionWorkflowStepIds.push(id)
    );
  });

  const pageKey = `[DOC]-userId${user?.id}-standardWorkflows`;
  const cookieParams: DocumentStandardWorkflowParam | undefined =
    getCookie(pageKey);

  const defaultDocumentStandardWorkflowParams: DocumentStandardWorkflowParam = {
    page: 0,
    limit: 10,
    sortBy: 'name',
    orderBy: ORDER.asc,
    disciplineIds: [],
    search: null
  };
  const [params, setParams] = useState<DocumentStandardWorkflowParam>({
    ...defaultDocumentStandardWorkflowParams,
    ...cookieParams
  });
  const [formatData, setFormatData] = useState<FormatData[]>([]);
  const [updatingData, setUpdatingData] = useState<FormatData | null>(null);

  const [selectedDocument, setSelectedDocument] = useState<Document | null>(
    null
  );
  const [openWorkflowDialog, setOpenWorkflowDialog] = useState<boolean>(false);

  const filteredDisciplines =
    disciplines.filter((discipline) =>
      params.disciplineIds.some(
        (disciplineId) => disciplineId === discipline.id
      )
    ) ?? [];

  useEffect(() => {
    const formatData: FormatData[] = [];
    documents.forEach(({ id: documentId, projectPositionWorkflowSteps }) => {
      projectPositionWorkflowSteps.forEach(
        ({ id: projectPositionWorkflowStepId }) =>
          formatData.push({ documentId, projectPositionWorkflowStepId })
      );
    });
    setFormatData(formatData);
    dispatch(loadingSlice.actions.setLoading({ showLoading: false }));
  }, [documents]);

  useEffect(() => {
    if (headers.length === 0) {
      dispatch(getWorkflowStepWithPositionsAsync());
    }
    loadingDocument(params);
  }, []);

  function loadingDocument(params: DocumentStandardWorkflowParam) {
    updateCookie(pageKey, params);
    dispatch(loadingSlice.actions.setLoading({ showLoading: true }));
    dispatch(getDocumentStandardWorkflowsAsync(params));
    setParams(params);
  }

  function handleChangePage<T>(
    newPage: number,
    params: T,
    loadingElement: (params: T) => void
  ) {
    loadingElement({
      ...params,
      page: newPage - 1 //The first value of pagination is 1, not 0
    });
  }

  function handleChangeLimit<T>(
    newLimit: number,
    params: T,
    loadingElement: (params: T) => void
  ) {
    loadingElement({
      ...params,
      page: 0,
      limit: newLimit
    });
  }

  function handleChangeDisciplines<T>(
    newDiscipline: Discipline[],
    params: T,
    loadingElement: (params: T) => void
  ) {
    const disciplineIds = newDiscipline.map((discipline) => discipline.id);
    loadingElement({
      ...params,
      page: 0,
      disciplineIds
    });
  }

  function findStandardWorkflow({
    documentId,
    projectPositionWorkflowStepId
  }: FormatData): boolean {
    return Boolean(
      formatData.find(
        (data) =>
          data.documentId === documentId &&
          data.projectPositionWorkflowStepId == projectPositionWorkflowStepId
      )
    );
  }

  function viewWorkflowDetails(document?: Document) {
    return function () {
      if (document) {
        setSelectedDocument(document);
        setOpenWorkflowDialog(true);
      } else {
        setOpenWorkflowDialog(false);
      }
    };
  }

  async function updateActiveDocument(documentId: number) {
    try {
      const indexOfDocument =
        documents.findIndex((document) => document.id === documentId) ?? -1;
      if (indexOfDocument >= 0) {
        const currentActive = documents[indexOfDocument].isActive;
        const document = { id: documentId, isActive: !currentActive };
        await dispatch(updateActiveDocumentAsync(document)).unwrap();
        dispatch(
          loadingSlice.actions.success({
            message: t('masterData.standardWorkflow.messages.statusUpdated')
          })
        );
      } else {
        throw new Error(
          t('masterData.standardWorkflow.messages.cannotUpdate') as string
        );
      }
    } catch (error) {
      if (error instanceof Error) {
        dispatch(
          loadingSlice.actions.error({
            message: error.message
          })
        );
      }
    }
  }

  async function updateStandardWorkflow({
    documentId,
    projectPositionWorkflowStepId
  }: FormatData): Promise<void> {
    try {
      const documentProjectPositionWorkflowStep = {
        documentId,
        projectPositionWorkflowStepId
      };
      const checked = findStandardWorkflow(documentProjectPositionWorkflowStep);

      setUpdatingData(documentProjectPositionWorkflowStep);
      if (!checked) {
        // Add new DocumentProjectPositionWorkflowStep
        await dispatch(
          createDocumentProjectPositionWorkflowStepAsync(
            documentProjectPositionWorkflowStep
          )
        ).unwrap();
        setFormatData([...formatData, documentProjectPositionWorkflowStep]);
      } else {
        // Remove DocumentProjectPositionWorkflowStep
        await dispatch(
          deleteDocumentProjectPositionWorkflowStepAsync(
            documentProjectPositionWorkflowStep
          )
        ).unwrap();
        const newFormatData = formatData.filter(
          (data) =>
            data.documentId !== documentId ||
            data.projectPositionWorkflowStepId !== projectPositionWorkflowStepId
        );
        setFormatData(newFormatData);
      }
      setTimeout(() => {
        setUpdatingData(null);
      }, 500);
    } catch {
      setUpdatingData(null);
    }
  }

  return (
    <div className={classes.root}>
      <Breadcrumb name="standardWorkflow" />
      <div className={classes.title}>
        <Typography className={classes.flexGrow} component="p" variant="h4">
          {t('masterData.standardWorkflow.title')}
        </Typography>
        <Chip
          variant="outlined"
          icon={hasPermission ? <EditOutlined /> : <VisibilityOutlined />}
          label={hasPermission ? t('masterData.standardWorkflow.editMode') : t('masterData.standardWorkflow.viewMode')}
        />
      </div>
      <EnhancedTableToolbar<DocumentStandardWorkflowParam>
        name={t('standardWorkflowView.toolbar.name')}
        params={params}
        sortOptions={getSortOptions(t)}
        loadingElement={loadingDocument}
        canSearchable
        searchHint={t('masterData.standardWorkflow.searchHint') as string}
      />
      <Paper className={classes.filterBox}>
        <Autocomplete
          className={clsx(classes.filterInput, classes.flexGrow)}
          id="discipline"
          options={disciplines}
          multiple
          getOptionLabel={(option) => option?.name}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                label={`${option?.name}`}
                {...getTagProps({ index })}
                variant="outlined"
                size="small"
                deleteIcon={<ClearRounded />}
              />
            ))
          }
          value={filteredDisciplines}
          getOptionSelected={(option, value) => option.id === value.id}
          onChange={(event, newValue) => {
            handleChangeDisciplines(newValue, params, loadingDocument);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              placeholder={
                filteredDisciplines.length === 0 ? (t('masterData.standardWorkflow.filterByDiscipline') as string) : ''
              }
            />
          )}
        />
        <FormControl
          className={classes.filterInput}
          variant="outlined"
          size="small"
        >
          <InputLabel id="limit-per-page-label">{t('masterData.standardWorkflow.itemsPerPage')}</InputLabel>
          <Select
            labelId="limit-per-page-label"
            value={params.limit}
            onChange={(event) =>
              handleChangeLimit(
                event.target.value as number,
                params,
                loadingDocument
              )
            }
            label={t('masterData.standardWorkflow.itemsPerPage')}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </FormControl>
      </Paper>
      <TableContainer component={Paper} className={classes.table}>
        <Table stickyHeader size="small">
          <TableHead className={classes.stickyHeader}>
            <TableRow>
              <TableCell
                className={clsx(classes.stickyColumn, classes.headerColor)}
                rowSpan={2}
              >
                {t('masterData.standardWorkflow.document')}
              </TableCell>
              {formatHeaders.map((workflowStep, index) => {
                const colSpan =
                  workflowStep.projectPositionWorkflowSteps.length;
                const name =
                  workflowStep.id == 4 // Consolidator step
                    ? `${t('standardWorkflow.creator')} / ${workflowStep.name}`
                    : workflowStep.name;
                return (
                  <TableCell
                    className={clsx(
                      classes.lineHeight120,
                      classes.workflowCell,
                      {
                        [classes.borderLeftCell]: index !== 0
                      }
                    )}
                    colSpan={colSpan}
                    key={workflowStep.id}
                  >
                    {name} →
                  </TableCell>
                );
              })}
            </TableRow>
            <TableRow>
              {formatHeaders.map(
                ({ projectPositionWorkflowSteps }, indexWorkflow) => {
                  return projectPositionWorkflowSteps.map(
                    ({ id, projectPosition }, indexPosition) => {
                      return (
                        <TableCell
                          className={clsx(classes.positionCell, {
                            [classes.borderLeftCell]:
                              indexPosition !== 0 || indexWorkflow !== 0
                          })}
                          padding="none"
                          align="center"
                          key={id}
                        >
                          <Typography
                            className={clsx(
                              classes.positionText,
                              classes.lineHeight120
                            )}
                          >
                            {projectPosition.name}
                          </Typography>
                        </TableCell>
                      );
                    }
                  );
                }
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.length > 0 ? (
              documents.map((document) => {
                const countSteps = formatData.filter(
                  (data) => data.documentId === document.id
                ).length;
                return (
                  <TableRow className={classes.tableRowHover} key={document.id}>
                    <TableCell
                      className={clsx(
                        classes.stickyColumn,
                        classes.documentCell
                      )}
                    >
                      <Typography className={classes.lineHeight120}>
                        {document.name}
                      </Typography>
                      <Typography
                        className={classes.lineHeight120}
                        variant="caption"
                        display="block"
                        gutterBottom
                      >
                        {document.no}
                      </Typography>
                      <div
                        className={clsx(classes.flexAlignCenter, classes.gap)}
                      >
                        <Chip
                          size="small"
                          variant="outlined"
                          label={document.discipline.name}
                        />
                        {countSteps === 0 ? (
                          <Tooltip title={t('masterData.standardWorkflow.noWorkflowSetup') as string} arrow>
                            <span className={classes.flexAlignCenter}>
                              <FaceRetouchingOffRounded
                                color="error"
                                fontSize="small"
                              />
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip
                            title={t('masterData.standardWorkflow.positionsInWorkflow', { count: countSteps }) as string}
                            arrow
                          >
                            <Chip
                              className={classes.workflowChip}
                              size="small"
                              variant="outlined"
                              label={countSteps}
                              icon={<FaceRounded />}
                              onClick={viewWorkflowDetails(document)}
                            />
                          </Tooltip>
                        )}
                        <div className={classes.flexGrow} />
                        <Tooltip
                          title={
                            document.isActive
                              ? (t('masterData.standardWorkflow.activeDocument', { hasPermission }) as string)
                              : (t('masterData.standardWorkflow.inactiveDocument', { hasPermission }) as string)
                          }
                          arrow
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => updateActiveDocument(document.id)}
                              disabled={!hasPermission}
                            >
                              {document.isActive ? (
                                <FiberManualRecord
                                  className={classes.greenColor}
                                />
                              ) : (
                                <FiberManualRecordOutlined color="action" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </div>
                    </TableCell>
                    {projectPositionWorkflowStepIds.map(
                      (projectPositionWorkflowStepId, index) => {
                        const isUpdatingData = Boolean(
                          updatingData &&
                          updatingData.documentId === document.id &&
                          updatingData.projectPositionWorkflowStepId ===
                          projectPositionWorkflowStepId
                        );

                        return (
                          <TableCell
                            className={clsx({
                              [classes.borderLeftCell]: index !== 0
                            })}
                            padding="checkbox"
                            align="center"
                            key={projectPositionWorkflowStepId}
                          >
                            {!isUpdatingData ? (
                              <Checkbox
                                className={classes.checkbox}
                                checked={findStandardWorkflow({
                                  documentId: document.id,
                                  projectPositionWorkflowStepId
                                })}
                                color="primary"
                                disabled={
                                  !hasPermission || Boolean(updatingData)
                                }
                                onClick={() =>
                                  updateStandardWorkflow({
                                    documentId: document.id,
                                    projectPositionWorkflowStepId
                                  })
                                }
                              />
                            ) : (
                              <CircularProgress size={24} />
                            )}
                          </TableCell>
                        );
                      }
                    )}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  className={clsx(
                    classes.fileNotFound,
                    classes.stickyColumn,
                    classes.documentCell
                  )}
                >
                  {getNotFoundText(params.search, t('common.document') as string, t('common.documents') as string, t)}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <div className={classes.pagination}>
        <Pagination
          count={Math.ceil(count / params.limit)}
          shape="rounded"
          page={params.page + 1}
          onChange={(event, newPage) => {
            handleChangePage<DocumentStandardWorkflowParam>(
              newPage,
              params,
              loadingDocument
            );
          }}
        />
      </div>
      {selectedDocument && (
        <Dialog
          fullWidth
          open={openWorkflowDialog}
          onClose={viewWorkflowDetails()}
        >
          <DialogTitle className={classes.dialogTitle}>
            <div className={classes.flexAlignCenter}>
              <Typography className={classes.flexGrow} variant="h6" noWrap>
                {t('masterData.standardWorkflow.dialog.title')}
              </Typography>
              <Tooltip title={t('common.close') as string} arrow>
                <IconButton
                  size="small"
                  edge="end"
                  onClick={viewWorkflowDetails()}
                >
                  <ClearRounded />
                </IconButton>
              </Tooltip>
            </div>
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <Alert severity="info">
              <span dangerouslySetInnerHTML={{ __html: t('masterData.standardWorkflow.dialog.description', { documentName: selectedDocument.name }) as string }} />
            </Alert>
            {formatHeaders.map((workflowStep) => {
              const projectPositionWorkflowStepsOfDocument = formatData.filter(
                ({ documentId }) => documentId === selectedDocument.id
              );
              const hasPositionInThisStep =
                projectPositionWorkflowStepsOfDocument.filter(
                  (projectPositionWorkflowStepOfDocument) =>
                    workflowStep.projectPositionWorkflowSteps.some(
                      (projectPositionWorkflowStep) =>
                        projectPositionWorkflowStep.id ===
                        projectPositionWorkflowStepOfDocument.projectPositionWorkflowStepId
                    )
                );
              const name =
                workflowStep.id == 4 // Consolidator step
                  ? `Creator / ${workflowStep.name}`
                  : workflowStep.name;
              if (hasPositionInThisStep.length > 0) {
                return (
                  <Paper variant="outlined" key={workflowStep.id}>
                    <div className={clsx(classes.flexAlignCenter, classes.gap)}>
                      <FaceRounded color="primary" fontSize="small" />
                      <Typography noWrap>{name}</Typography>
                    </div>
                    <Typography
                      className={classes.lineHeight120}
                      display="block"
                      variant="caption"
                      gutterBottom
                    >
                      {workflowStep.projectPositionWorkflowSteps
                        .filter((projectPositionWorkflowStep) =>
                          projectPositionWorkflowStepsOfDocument.some(
                            ({ projectPositionWorkflowStepId }) =>
                              projectPositionWorkflowStepId ===
                              projectPositionWorkflowStep.id
                          )
                        )
                        .map(({ projectPosition }) => {
                          return projectPosition.name;
                        })
                        .join(' • ')}
                    </Typography>
                  </Paper>
                );
              }
            })}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 2, 0)
    },
    title: {
      display: 'flex',
      alignItems: 'center',
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column',
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    filterBox: {
      display: 'flex',
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column'
      },
      gap: theme.spacing(1),
      padding: theme.spacing(1),
      marginBottom: theme.spacing(1),
      borderRadius: theme.spacing(1)
    },
    filterInput: {
      minWidth: 144,
      '& .MuiInputBase-root': {
        height: 40,
        [theme.breakpoints.down('xs')]: {
          height: 'auto'
        }
      }
    },
    table: {
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2),
      maxHeight: window.innerHeight - 80
    },
    stickyHeader: {
      position: 'sticky',
      top: 0,
      background: theme.palette.background.default,
      zIndex: 10
    },
    stickyColumn: {
      position: 'sticky',
      left: 0,
      background: theme.palette.background.paper,
      borderRight: `1px solid ${theme.palette.divider}`,
      zIndex: 5
    },
    headerColor: {
      background: theme.palette.background.default
    },
    workflowChip: {
      '& > *': {
        '&.MuiSvgIcon-root': {
          width: 16,
          height: 16,
          marginLeft: theme.spacing(0.5)
        },
        '&.MuiChip-label': {
          padding: theme.spacing(0, 0.75)
        }
      }
    },
    workflowCell: {
      padding: theme.spacing(1, 1.5, 0.5, 1.5)
    },
    positionCell: {
      padding: theme.spacing(0.5)
    },
    documentCell: {
      padding: theme.spacing(1)
    },
    checkbox: {
      '&.Mui-disabled': {
        color: theme.palette.action.active
      }
    },
    tableRowHover: {
      '&:hover, &.Mui-focusVisible': {
        '& > *': {
          background: `linear-gradient(${fade(
            theme.palette.text.primary,
            0.05
          )}, ${fade(theme.palette.text.primary, 0.05)}), linear-gradient(${theme.palette.background.paper
            }, ${theme.palette.background.paper})`
        }
      }
    },
    borderLeftCell: {
      borderLeft: `1px solid ${theme.palette.divider}`
    },
    positionText: {
      fontSize: 12
    },
    lineHeight120: {
      lineHeight: 1.2
    },
    fileNotFound: {
      padding: theme.spacing(2, 1),
      textAlign: 'center'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(1.5)
    },
    dialogTitle: {
      padding: theme.spacing(2, 2, 1, 2.5)
    },
    dialogContent: {
      padding: theme.spacing(0, 2, 2, 2),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      '& > *:not(:first-child)': {
        padding: theme.spacing(0.5, 1),
        borderRadius: theme.spacing(1)
      }
    },
    gap: {
      gap: theme.spacing(0.5)
    },
    flexAlignCenter: {
      display: 'flex',
      alignItems: 'center'
    },
    flexGrow: {
      flexGrow: 1
    },
    greenColor: {
      color: theme.palette.success.main
    }
  })
);
